import { serializeAddress, validateCryptoAddress } from "@repo/config";
import type { EthOrSolanaAssetIdFormat } from "@repo/types";
import z from "zod/v4";

const STRICT_EVM_ACCOUNT_ADDRESS_PATTERN = /^0x[0-9a-fA-F]{40}$/;
const ZERO_EVM_ACCOUNT_ADDRESS = "0x0000000000000000000000000000000000000000";
const ALL_FS_EVM_ACCOUNT_ADDRESS = "0xffffffffffffffffffffffffffffffffffffffff";

// Re-export from @repo/config for backwards compatibility
export {
  validateCryptoAddress,
  serializeAddress,
  validateAndNormalizeAddress,
  validateAndNormalizeCaipAccountId,
} from "@repo/config";

export const validateAssetAddress = (
  address: EthOrSolanaAssetIdFormat,
): boolean => {
  const [id, chainId] = address.split(":");
  if (!id || !chainId) {
    throw new Error(`Invalid asset ID format: ${address}`);
  }
  const validatedAddress = validateCryptoAddress(id);
  if (!validatedAddress.isValid) {
    throw new Error(`Invalid asset ID format: ${address}`);
  }
  return validatedAddress.isValid;
};

export const isValidEthOrSolanaAddress = (address: string) => {
  return validateCryptoAddress(address).isValid;
};

/**
 * Check if a string is a valid Solana address using runtime validation.
 *
 * Note: This is different from the type guard `isSolanaAddress` in @repo/types
 * which narrows a branded `Address` type. This function validates raw strings.
 */
export const isValidSolanaAddress = (address: string): boolean => {
  const result = validateCryptoAddress(address);
  return result.isValid && result.type === "solana";
};

/**
 * Check if a string is a valid EVM address using runtime validation.
 *
 * Note: This is different from the type guard `isEvmAddress` in @repo/types
 * which narrows a branded `Address` type. This function validates raw strings.
 */
export const isValidEvmAddress = (address: string): boolean => {
  const result = validateCryptoAddress(address);
  return result.isValid && result.type === "evm";
};

/**
 * Rejects malformed and sentinel EVM account addresses that must never be
 * persisted as portfolio accounts.
 */
export const isDegenerateEvmAccountAddress = (address: string): boolean => {
  if (
    address.length !== 42 ||
    !STRICT_EVM_ACCOUNT_ADDRESS_PATTERN.test(address)
  ) {
    return true;
  }

  const normalizedAddress = address.toLowerCase();
  return (
    normalizedAddress === ZERO_EVM_ACCOUNT_ADDRESS ||
    normalizedAddress === ALL_FS_EVM_ACCOUNT_ADDRESS
  );
};

// Backwards compatibility aliases (deprecated)
/** @deprecated Use isValidSolanaAddress instead */
export const isSolanaAddress = isValidSolanaAddress;
/** @deprecated Use isValidEvmAddress instead */
export const isEvmAddress = isValidEvmAddress;

export const EthOrSolanaAddressZodType = z.custom<string>(
  (value): value is string =>
    typeof value === "string" && isValidEthOrSolanaAddress(value),
  { error: "Invalid Address" },
);

export const SolanaAddressZodType = z.custom<string>(
  (value): value is string =>
    typeof value === "string" && isValidSolanaAddress(value),
  { error: "Must be a valid Solana address" },
);
